// Exploration Data - 探索数据管理
// 统一存储所有场景、事件、彩蛋等数据，便于集中管理

// 常量定义
export const LOCATION_NAMES = {
  UNKNOWN: '未知之地',
  CAVE: '洞府',
  BACK_MOUNTAIN: '后山',
  SECRET_REALM: '秘境',
  CLOUD_SEA: '云海',
  FORBIDDEN: '禁地'
};

// 可前往地点配置
export const availableLocations = [
  {
    name: LOCATION_NAMES.UNKNOWN,
    description: '神秘的未知区域，充满机遇与危险，适合勇于探索的修士',
    features: ['探索', '冒险', '机缘', '危险']
  },
  {
    name: LOCATION_NAMES.CAVE,
    description: '你的修炼居所，可以在这里安心修炼、炼丹炼器',
    features: ['修炼', '炼丹', '炼器']
  },
  {
    name: LOCATION_NAMES.BACK_MOUNTAIN,
    description: '宗门后山，适合初级弟子历练和采集灵草',
    features: ['采集', '历练', '战斗', '探索']
  },
  {
    name: LOCATION_NAMES.SECRET_REALM,
    description: '充满机遇与危险的秘境，可能遇到珍贵资源或强大敌人',
    features: ['机缘', '危险', '宝藏', '历练']
  },
  {
    name: LOCATION_NAMES.CLOUD_SEA,
    description: '飘渺云海，适合修炼心境和感悟天地之道',
    features: ['感悟', '修炼', '机缘', '平静']
  },
  {
    name: LOCATION_NAMES.FORBIDDEN,
    description: '宗门禁地，危机四伏但机遇巨大，需要足够实力才能进入',
    features: ['危险', '宝藏', '历练', '地图探索']
  }
];

export const explorationScenes = {
  [LOCATION_NAMES.UNKNOWN]: {
    name: '未知之地',
    description: '这里是一片完全未知的区域，雾气缭绕，神秘而危险。你感受到一股未知的力量在召唤着你...',
    background: '未知之地是未知的特殊区域，充满机遇与危险。传闻此处曾是上古战场，埋藏着无数宝藏和秘密。修士们常在此历练，但也有人一去不返。未知之地的时间流速不稳定，有时一日可抵外界十日，有时则相反。',
    baseEvents: ['四处查看', '仔细观察', '聆听声音']
  },
  [LOCATION_NAMES.CAVE]: {
    name: '宁静洞府',
    description: '洞府周围灵气氤氲，远处山峦起伏。近处有一片灵田，洞壁上似乎有古老的刻痕，空气中弥漫着淡淡的药香。你心中涌起探索的冲动...',
    background: '此洞府乃前辈修士所留，内有修炼静室、丹房药圃。洞中灵脉汇聚，适合筑基期修士闭关修炼。四周布有简单的护山阵法，可阻挡寻常野兽侵扰。洞府深处隐有传承玉简，记载上古炼丹秘术。',
    baseEvents: ['观察洞壁', '探索花园', '检查丹炉']
  },
  [LOCATION_NAMES.BACK_MOUNTAIN]: {
    name: '宗门后山',
    description: '后山树木茂密，山风呼啸。远处传来野兽的吼声，山路崎岖难行。你感受到一股原始的野性气息...',
    background: '后山乃宗门试炼之地，山中有诸多妖兽灵禽栖息。山势险峻，怪石嶙峋，传闻曾有金丹修士在此渡劫飞升。山巅有古刹遗迹，供奉历代祖师牌位。山中盛产珍稀矿石和灵草，适合历练弟子心性。',
    baseEvents: ['伐木采集', '追踪野兽', '攀登山巅']
  },
  [LOCATION_NAMES.SECRET_REALM]: {
    name: '仙家秘境',
    description: '秘境之内仙气缭绕，奇花异草遍地。空中飘浮着灵光，你仿佛置身于仙境之中...',
    background: '秘境为上古仙人开辟的小千世界，内有天地灵根，万年不枯。秘境之中时间流速与外界不同，一日可抵外界三日。内有仙府遗址、传承殿堂，藏有诸多失传功法。秘境入口受阵法庇护，非元婴修士难以强行突破。',
    baseEvents: ['采摘灵花', '探索遗迹', '感悟天地']
  },
  [LOCATION_NAMES.CLOUD_SEA]: {
    name: '飘渺云海',
    description: '云海之上白雾茫茫，脚下是无尽的云层。远处有雷鸣之声，你感受到天地法则的力量...',
    background: '云海位于九天之上，乃天地交汇之地。云层之中蕴含纯阳之气，可助修士淬炼神魂。雷云区域乃天劫汇聚之所，内藏雷灵珠等天材地宝。云海深处有仙宫遗迹，传为太古雷神所居。修士在此可感悟天道法则，突破境界瓶颈。',
    baseEvents: ['观云悟道', '穿越雷云', '采集云露']
  }
};

// 探索事件结果配置
export const explorationEventResults = {
  // 未知之地选项结果
  '四处查看': [
    { prob: 0.4, effect: () => [{ action: 'addItem', itemName: '灵石', count: Math.floor(Math.random() * 2) + 1 }, { message: `你发现了一些散落的【灵石】。`, isItemGain: true }], discovery: '散落灵石' },
    { prob: 0.7, effect: () => gainXp(20).concat(['你感受到未知之地的神秘力量。']) },
    { prob: 0.9, effect: () => ['周围雾气太浓，你什么都看不到。'] },
    { prob: 1.0, effect: () => [{ action: 'addItem', itemName: '神秘宝物' }, { message: '你发现了一个【神秘宝物】！', isItemGain: true }], discovery: '神秘宝物' }
  ],
  '仔细观察': [
    { prob: 0.5, effect: () => [{ action: 'increaseAttribute', attribute: 'divineSenseStrength', value: 2 }, '未知之地的气息让你神识增强。'] },
    { prob: 0.8, effect: () => gainXp(25).concat(['你观察到了未知之地的独特景象。']) },
    { prob: 1.0, effect: () => ['你发现了隐藏的痕迹！'], discovery: '隐藏痕迹' }
  ],
  '聆听声音': [
    { prob: 0.6, effect: () => ['你听到远处传来的神秘声音，似乎在指引着什么。'] },
    { prob: 0.8, effect: () => [{ action: 'updateAttribute', attribute: 'qi', value: Math.max(0, getCurrentQi() - 15) }, '神秘声音干扰了你的心神，你消耗了一些气血。'] },
    { prob: 1.0, effect: () => learnSkill('感知术').concat(['神秘声音传授给你感知术的知识。']), discovery: '神秘指引' }
  ],

  // 洞府选项结果
  '观察洞壁': [
    { prob: 0.6, effect: () => gainXp(15).concat(['你发现了一些古老的符文，获得了启发。']), discovery: '远古符文' },
    { prob: 0.9, effect: () => ['洞壁风化严重，没有发现特别的东西。'] },
    { prob: 1.0, effect: () => [{ action: 'addItem', itemName: '灵石' }, { message: '你发现了一块隐藏的【灵石】！', isItemGain: true }], discovery: '隐藏灵石' }
  ],
  '探索花园': [
    { prob: 0.7, effect: () => [{ action: 'addItem', itemName: '灵草', count: Math.floor(Math.random() * 3) + 1 }, { message: `你发现了${Math.floor(Math.random() * 3) + 1}株【灵草】！`, isItemGain: true }], discovery: '灵草花园' },
    { prob: 0.9, effect: () => ['花园中只有一些小动物痕迹。'] },
    { prob: 1.0, effect: () => [{ action: 'increaseAttribute', attribute: 'divineSenseStrength', value: 1 }, '花园中央的灵泉让你神识增强！'], discovery: '灵泉' }
  ],
  '检查丹炉': [
    { prob: 0.5, effect: () => learnSkill('炼丹术').concat(['你从丹炉中学到了炼丹知识。']), discovery: '炼丹心得' },
    { prob: 0.8, effect: () => [{ action: 'addItem', itemName: '灵草' }, { message: '丹炉中有残留的药渣，你获得了一些【灵草】。', isItemGain: true }] },
    { prob: 1.0, effect: () => ['丹炉中飞出一个火灵！'], discovery: '火灵指引' }
  ],

  // 后山选项结果
  '伐木采集': [
    { prob: 0.5, effect: () => [{ action: 'addItem', itemName: '木材', count: Math.floor(Math.random() * 3) + 1 }, { message: `你采集到了一些【木材】。`, isItemGain: true }] },
    { prob: 0.7, effect: () => [{ action: 'addItem', itemName: '血参', count: Math.floor(Math.random() * 2) + 1 }, { message: `你在树根下发现了【血参】！`, isItemGain: true }] },
    { prob: 0.9, effect: () => [{ action: 'addItem', itemName: '玄铁矿', count: 1 }, { message: `你发现了一些【玄铁矿】石。`, isItemGain: true }] },
    { prob: 1.0, effect: () => gainXp(20).concat(['你发现了一棵灵木！']), discovery: '灵木' }
  ],
  '追踪野兽': [
    { prob: 0.6, effect: () => [{ action: 'addItem', itemName: '肉食' }, { message: '你追踪到一只野兔，获得了一些【肉食】。', isItemGain: true }] },
    { prob: 0.8, effect: () => ['野兽非常警觉，你没有追上它。'] },
    { prob: 1.0, effect: () => ['你遇到了一只灵兽！它愿意成为你的伙伴。'], discovery: '灵兽伙伴' }
  ],
  '攀登山巅': [
    { prob: 0.4, effect: () => [{ action: 'addItem', itemName: '雪莲', count: 1 }, { message: '你在山巅发现了【雪莲】！', isItemGain: true }] },
    { prob: 0.7, effect: () => gainXp(25).concat(['山巅风光无限，你获得了心灵的升华。']), discovery: '山巅视野' },
    { prob: 0.9, effect: () => [{ action: 'updateAttribute', attribute: 'qi', value: Math.max(0, getCurrentQi() - 10) }, '山路崎岖，你消耗了一些气血。'] },
    { prob: 1.0, effect: () => learnSkill('修炼术').concat(['山巅有古老的石碑，记录着修炼心得。']), discovery: '石碑秘籍' }
  ],

  // 秘境选项结果
  '采摘灵花': [
    { prob: 0.8, effect: () => [{ action: 'addItem', itemName: '灵花', count: Math.floor(Math.random() * 2) + 1 }, { message: `你采摘到了一些【灵花】。`, isItemGain: true }] },
    { prob: 0.9, effect: () => ['灵花周围有阵法保护，你无法靠近。'] },
    { prob: 1.0, effect: () => gainXp(30).concat(['你发现了一株仙品灵花！']), discovery: '仙品灵花' }
  ],
  '探索遗迹': [
    { prob: 0.6, effect: () => learnSkill('机关术').concat(['遗迹中有古老的机关，你获得了机关术知识。']), discovery: '机关遗迹' },
    { prob: 0.8, effect: () => ['遗迹布满灰尘，没有发现有价值的东西。'] },
    { prob: 1.0, effect: () => [{ action: 'addItem', itemName: '灵石', count: 5 }, { message: '遗迹深处藏有【灵石】宝藏！', isItemGain: true }], discovery: '遗迹宝藏' }
  ],
  '感悟天地': [
    { prob: 0.7, effect: () => gainXp(20).concat(['天地灵气充裕，你获得了修炼加速。']) },
    { prob: 0.9, effect: () => [{ action: 'increaseAttribute', attribute: 'divineSenseStrength', value: 2 }, '秘境中的灵气让你神识略有提升。'] },
    { prob: 1.0, effect: () => ['你领悟了天地法则！'], discovery: '天地法则' }
  ],

  // 云海选项结果
  '观云悟道': [
    { prob: 0.6, effect: () => gainXp(18).concat(['云海中的景象让你悟道有成。']) },
    { prob: 0.9, effect: () => ['云层变化莫测，你没有特别的收获。'] },
    { prob: 1.0, effect: () => ['你看到了仙人留下的云中文字！'], discovery: '云中仙文' }
  ],
  '穿越雷云': [
    { prob: 0.4, effect: () => [{ action: 'updateAttribute', attribute: 'qi', value: Math.max(0, getCurrentQi() - 20) }, '雷云威力巨大，你受到了雷击伤害。'] },
    { prob: 0.8, effect: () => gainXp(25).concat(['你成功穿越了雷云，获得了雷灵之力。']), discovery: '雷灵之力' },
    { prob: 1.0, effect: () => ['雷云中隐藏着雷劫秘宝！'], discovery: '雷劫秘宝' }
  ],
  '采集云露': [
    { prob: 0.7, effect: () => [{ action: 'addItem', itemName: '云露', count: Math.floor(Math.random() * 2) + 1 }, { message: `你采集到了一些【云露】。`, isItemGain: true }] },
    { prob: 0.9, effect: () => ['云露太稀薄，你没有采集到。'] },
    { prob: 1.0, effect: () => [{ action: 'increaseAttribute', attribute: 'mana', value: 10 }, '你采集到了仙品云露！'], discovery: '仙品云露' }
  ]
};

// 辅助函数（需要外部提供实现）
function gainXp(amount) {
  return [{ type: 'xp', value: amount }];
}

function learnSkill(skillName) {
  return [{ type: 'skill', skillName }];
}

function getCurrentQi() {
  // 需要外部状态访问
  return 100;
}

export const randomEvents = [
  // 随机奇遇类
  {
    id: 'encounter_hermit',
    type: 'encounter',
    name: '隐世高人',
    description: '你遇到一位隐世修炼的高人，他愿意指点你一二。',
    probability: 0.3,
    effects: [
      { type: 'xp', value: 50 },
      { type: 'skill', skillName: '修炼心得', level: 1 }
    ],
    narrative: '高人微微一笑，传授给你一些修炼的心得。'
  },
  {
    id: 'encounter_spirit_beast',
    type: 'encounter',
    name: '灵兽相伴',
    description: '一只可爱的灵兽出现在你面前，似乎对你很有好感。',
    probability: 0.25,
    effects: [
      { type: 'item', itemName: '灵兽蛋', count: 1 },
      { type: 'attribute', attribute: 'luck', value: 5 }
    ],
    narrative: '灵兽轻轻蹭着你的腿，眼中满是信任。'
  },
  {
    id: 'encounter_bandits',
    type: 'encounter',
    name: '山贼拦路',
    description: '一群山贼突然出现，挡住了你的去路。',
    probability: 0.2,
    effects: [
      { type: 'combat', enemy: 'bandits', reward: ['灵石', '武器'] },
      { type: 'qi', value: -30 }
    ],
    narrative: '山贼们手持兵器，眼中闪烁着贪婪的光芒。'
  },

  // 环境互动类
  {
    id: 'interact_ancient_tree',
    type: 'interaction',
    name: '古树对话',
    description: '一棵古老的树木似乎拥有灵智，你可以与之交流。',
    probability: 0.4,
    effects: [
      { type: 'wisdom', value: 10 },
      { type: 'item', itemName: '树灵果', count: 2 }
    ],
    narrative: '古树摇曳着枝叶，仿佛在讲述着远古的故事。'
  },
  {
    id: 'interact_spirit_spring',
    type: 'interaction',
    name: '灵泉洗礼',
    description: '清澈的灵泉散发着柔和的光芒，邀请你沐浴其中。',
    probability: 0.35,
    effects: [
      { type: 'attribute', attribute: 'divineSenseStrength', value: 3 },
      { type: 'purify', value: 20 }
    ],
    narrative: '灵泉之水洗涤着你的身心，让你感到无比清爽。'
  },
  {
    id: 'interact_weather_stone',
    type: 'interaction',
    name: '气象石',
    description: '一块能影响天气的奇异石头，你可以尝试操控它。',
    probability: 0.3,
    effects: [
      { type: 'weather_change', weather: 'clear' },
      { type: 'xp', value: 30 }
    ],
    narrative: '气象石在你的操控下，天空变得晴朗起来。'
  },

  // 谜题挑战类
  {
    id: 'puzzle_rune_circle',
    type: 'puzzle',
    name: '符文圆阵',
    description: '地面上有一个复杂的符文圆阵，需要解开才能获得奖励。',
    probability: 0.25,
    effects: [
      { type: 'treasure', treasureName: '符文宝箱' },
      { type: 'xp', value: 40 }
    ],
    narrative: '符文圆阵缓缓开启，露出里面的宝藏。'
  },
  {
    id: 'puzzle_illusion_maze',
    type: 'puzzle',
    name: '幻境迷宫',
    description: '你陷入了一个幻境迷宫，需要找到出口。',
    probability: 0.2,
    effects: [
      { type: 'attribute', attribute: 'intelligence', value: 5 },
      { type: 'item', itemName: '幻境结晶', count: 1 }
    ],
    narrative: '你成功走出幻境，获得了宝贵的经验。'
  },

  // 收集资源类
  {
    id: 'collect_rare_herb',
    type: 'collection',
    name: '珍稀灵草',
    description: '你发现了一株极为罕见的灵草，散发着耀眼的光芒。',
    probability: 0.4,
    effects: [
      { type: 'item', itemName: '九叶灵芝', count: 1 },
      { type: 'luck', value: 10 }
    ],
    narrative: '这株灵草蕴含着庞大的灵力，让你兴奋不已。'
  },
  {
    id: 'collect_spirit_crystal',
    type: 'collection',
    name: '灵石矿脉',
    description: '地下隐藏着丰富的灵石矿脉，你可以挖掘一些。',
    probability: 0.35,
    effects: [
      { type: 'item', itemName: '上品灵石', count: Math.floor(Math.random() * 5) + 1 },
      { type: 'skill', skillName: '挖矿术', level: 1 }
    ],
    narrative: '矿脉中闪烁着灵石的光芒，你收获颇丰。'
  },

  // 特殊事件类
  {
    id: 'special_festival',
    type: 'special',
    name: '仙界节日',
    description: '今天是仙界的特殊节日，四周弥漫着喜庆的气氛。',
    probability: 0.1,
    effects: [
      { type: 'buff', buffName: '节日祝福', duration: 24 },
      { type: 'xp', value: 100 }
    ],
    narrative: '仙音袅袅，祥瑞降临，让你感受到仙界的恩赐。'
  },
  {
    id: 'special_comet',
    type: 'special',
    name: '流星奇观',
    description: '夜空中划过一道璀璨的流星，你可以许下愿望。',
    probability: 0.15,
    effects: [
      { type: 'wish', wishes: 1 },
      { type: 'attribute', attribute: 'luck', value: 20 }
    ],
    narrative: '流星划过天际，你的愿望仿佛得到了回应。'
  },

  // 时间相关
  {
    id: 'time_sunset',
    type: 'time',
    name: '日落时分',
    description: '夕阳西下，天地间染上了一层金色的光芒。',
    probability: 0.5,
    effects: [
      { type: 'mood', mood: 'peaceful' },
      { type: 'inspiration', value: 15 }
    ],
    narrative: '夕阳的余晖让你感受到生命的美好。'
  },
  {
    id: 'time_midnight',
    type: 'time',
    name: '午夜时分',
    description: '夜深人静，万籁俱寂。你感受到一股神秘的力量。',
    probability: 0.3,
    effects: [
      { type: 'attribute', attribute: 'divineSenseStrength', value: 2 },
      { type: 'mystery', value: 10 }
    ],
    narrative: '午夜的宁静让你更容易感知天地间的奥秘。'
  }
];

// 彩蛋和隐藏事件
export const easterEggs = [
  {
    id: 'hidden_cave',
    trigger: '在洞府连续探索10次',
    name: '隐藏洞窟',
    description: '你发现了一个隐藏的洞窟，里面藏有宝藏。',
    effects: [
      { type: 'treasure', treasureName: '洞窟宝藏' },
      { type: 'achievement', achievement: '探险家' }
    ]
  },
  {
    id: 'secret_technique',
    trigger: '在秘境中找到特定的符文组合',
    name: '秘传功法',
    description: '你破解了符文，获得了失传已久的功法。',
    effects: [
      { type: 'skill', skillName: '秘传功法', level: 5 },
      { type: 'attribute', attribute: 'cultivationSpeed', value: 20 }
    ]
  },
  {
    id: 'spirit_friendship',
    trigger: '连续喂养灵兽5次',
    name: '灵兽契约',
    description: '灵兽与你建立了深厚的友谊，成为你的伙伴。',
    effects: [
      { type: 'companion', companion: '灵兽伙伴' },
      { type: 'buff', buffName: '伙伴加成', duration: -1 }
    ]
  }
];

// 妖兽遭遇概率配置
export const beastEncounterRates = {
  [LOCATION_NAMES.UNKNOWN]: { '四处查看': 0.3, '仔细观察': 0.2, '聆听声音': 0.4 },
  [LOCATION_NAMES.BACK_MOUNTAIN]: { '伐木采集': 0.25, '追踪野兽': 0.4, '攀登山巅': 0.15 },
  [LOCATION_NAMES.SECRET_REALM]: { '采摘灵花': 0.2, '探索遗迹': 0.3, '感悟天地': 0.1 },
  [LOCATION_NAMES.CLOUD_SEA]: { '观云悟道': 0.15, '穿越雷云': 0.35, '采集云露': 0.2 },
  [LOCATION_NAMES.FORBIDDEN]: { '四处查看': 0.5, '仔细观察': 0.4, '聆听声音': 0.3 }
};

// 地点特定事件权重
export const locationEventWeights = {
  [LOCATION_NAMES.UNKNOWN]: {
    'encounter_hermit': 0.3,
    'interact_weather_stone': 0.2,
    'puzzle_illusion_maze': 0.2,
    'collect_spirit_crystal': 0.2,
    'time_midnight': 0.4
  },
  [LOCATION_NAMES.CAVE]: {
    'encounter_hermit': 0.4,
    'interact_ancient_tree': 0.3,
    'puzzle_rune_circle': 0.2,
    'collect_rare_herb': 0.3,
    'time_sunset': 0.5
  },
  [LOCATION_NAMES.BACK_MOUNTAIN]: {
    'encounter_spirit_beast': 0.4,
    'encounter_bandits': 0.3,
    'interact_weather_stone': 0.2,
    'collect_spirit_crystal': 0.4,
    'time_midnight': 0.3
  },
  [LOCATION_NAMES.SECRET_REALM]: {
    'interact_spirit_spring': 0.4,
    'puzzle_illusion_maze': 0.3,
    'collect_rare_herb': 0.3,
    'special_comet': 0.1,
    'time_sunset': 0.4
  },
  [LOCATION_NAMES.CLOUD_SEA]: {
    'special_festival': 0.1,
    'interact_weather_stone': 0.4,
    'puzzle_rune_circle': 0.2,
    'time_midnight': 0.5,
    'special_comet': 0.2
  }
};
